// [S-339] Deal-workflow data hooks — back the Perks Register screen against
// the S-336 admin routes (thin HTTP over the S-332 deterministic services):
//   GET /deals/:id            → the deal
//   GET /deals/:id/register   → the perks register (rows + expansion data)
//   GET /deals/:id/cap-sheet  → cap-sheet impact (G/Y/R + aggregate exposure)
//   GET /deals/:id/cascade    → slate-cascade exposure
//   GET /projects/:id         → project aggregate (for the picture title)
// Tenant scope is resolved server-side from the caller's session.
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "./api";

// ─── Wire types — mirror the S-332 service outputs the S-336 routes emit ──
export type CapSheetImpact = "green" | "yellow" | "red";

export type EquityCellWire = {
  lineItemId: string;
  subcategoryId: string;
  talentAsk: string;
  studioOffer: string;
  status: string;
};

export type RegisterRowWire = {
  lineItemId: string;
  category: string;
  subcategoryId: string;
  studioOffer: string;
  talentAsk: string;
  priorPrecedent: string | null;
  isNewCategory: boolean;
  intraProjectComparable: EquityCellWire[];
  capSheetImpact: CapSheetImpact | null;
  riskFlag: "low" | "medium" | "high" | "critical" | null;
  deptOwner: string[];
  status: string;
};

export type PerksRegisterWire = {
  dealId: string;
  rows: RegisterRowWire[];
};

export type CapSheetScoreWire = {
  dealId: string;
  lines: {
    lineItemId: string;
    category: string;
    costUsd: number | null;
    impact: CapSheetImpact | null;
  }[];
  aggregateRedExposureUsd: number;
  contingencyDrawRequired: boolean;
};

export type CascadeAssessmentWire = {
  dealId: string;
  comparableDealCount: number;
  newCategories: {
    lineItemId: string;
    subcategoryId: string;
    reason: string;
    exposureUsd: number;
  }[];
  totalExposureUsd: number;
};

export type DealWire = {
  id: string;
  projectId: string;
  tenantId: string;
  talentPartyId: string;
  agentPartyId: string | null;
  tier: string;
  lifecycleState: string;
};

export type ProjectAggregateWire = {
  project: {
    id: string;
    tenantId: string;
    title: string;
    budgetTotal: number;
    productionStart: string;
    status: string;
  };
  capSheet: unknown;
  deals: DealWire[];
  cast: { id: string; name: string; kind: string }[];
  lineItemsByDeal: Record<string, unknown[]>;
};

export function useDeal(dealId: string): UseQueryResult<DealWire, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["deal", dealId],
    enabled: dealId.length > 0,
    queryFn: () => api<DealWire>(`/deals/${encodeURIComponent(dealId)}`),
  });
}

export function useDealRegister(
  dealId: string
): UseQueryResult<PerksRegisterWire, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["deal", dealId, "register"],
    enabled: dealId.length > 0,
    queryFn: () =>
      api<PerksRegisterWire>(`/deals/${encodeURIComponent(dealId)}/register`),
  });
}

export function useDealCapSheet(
  dealId: string
): UseQueryResult<CapSheetScoreWire, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["deal", dealId, "cap-sheet"],
    enabled: dealId.length > 0,
    queryFn: () =>
      api<CapSheetScoreWire>(`/deals/${encodeURIComponent(dealId)}/cap-sheet`),
  });
}

export function useDealCascade(
  dealId: string
): UseQueryResult<CascadeAssessmentWire, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["deal", dealId, "cascade"],
    enabled: dealId.length > 0,
    queryFn: () =>
      api<CascadeAssessmentWire>(`/deals/${encodeURIComponent(dealId)}/cascade`),
  });
}

export function useProjectAggregate(
  projectId: string | undefined
): UseQueryResult<ProjectAggregateWire, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["project", projectId],
    enabled: projectId !== undefined && projectId.length > 0,
    queryFn: () =>
      api<ProjectAggregateWire>(`/projects/${encodeURIComponent(projectId!)}`),
  });
}

/** Whole-USD → "$1.8M" / "$620K" / "$0". */
export function formatUsdShort(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}
